import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { BookingCalendar, CalendarLegend, MonthNav } from "../components/BookingCalendar";
import { AlertTriangle, Waves, FileText } from "lucide-react";

function firstMonthOfSeason() {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  // If we're in Feb-July -> jump to next August
  if (m >= 1 && m <= 6) return { month: 7, year: y }; // August
  return { month: m, year: y };
}

export const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

export default function Booking() {
  const [params, setParams] = useSearchParams();
  const [huts, setHuts] = useState([]);
  const [hut, setHut] = useState(null);
  const start = firstMonthOfSeason();
  const [month, setMonth] = useState(start.month);
  const [year, setYear] = useState(start.year);
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reg, setReg] = useState({});
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", age: "", address: "", guests: 1, occupants: [{ first_name: "", last_name: "", permit_number: "" }], regulations_accepted: false, boat_confirmed: false });
  const [quota, setQuota] = useState(null);
  const [regOpened, setRegOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/huts").then(r => setHuts(r.data)); api.get("/content").then(r => setReg(r.data.regulations || {})); }, []);
  useEffect(() => {
    if (!huts.length) return;
    const slug = params.get("hut");
    const h = huts.find(x => x.slug === slug) || huts.find(x => x.status === "ACTIVE") || huts[0];
    setHut(h);
  }, [huts, params]);

  useEffect(() => {
    if (!hut) return;
    const startD = fmtDate(new Date(year, month, 1));
    const endD = fmtDate(new Date(year, month+1, 0));
    api.get(`/calendar/${hut.id}`, { params: { start: startD, end: endD } }).then(r => setDays(r.data.days));
  }, [hut, month, year]);

  useEffect(() => {
    if (!selected || !form.email) { setQuota(null); return; }
    api.get("/quota", { params: { email: form.email, date: selected } }).then(r => setQuota(r.data)).catch(()=>{});
  }, [selected, form.email]);

  const setGuests = (n) => {
    const occupants = Array.from({ length: n }, (_, i) => form.occupants[i] || { first_name: "", last_name: "", permit_number: "" });
    setForm({ ...form, guests: n, occupants });
  };
  const setOccupant = (i, k, v) => {
    const occupants = form.occupants.map((o, j) => j === i ? { ...o, [k]: v } : o);
    setForm({ ...form, occupants });
  };

  const canSubmit = useMemo(() => {
    if (!hut || !selected || submitting) return false;
    if (!form.first_name || !form.last_name || !form.email || !form.phone) return false;
    if (!form.age || !form.address.trim()) return false;
    if (form.occupants.length !== form.guests || form.occupants.some(o => !o.first_name.trim() || !o.last_name.trim())) return false;
    if (!form.regulations_accepted) return false;
    if (hut.boat_required && !form.boat_confirmed) return false;
    if (quota && quota.used >= 4) return false;
    return true;
  }, [hut, selected, form, quota, submitting]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations/checkout", {
        hut_id: hut.id, date: selected, ...form, age: Number(form.age),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (!hut) return <div className="max-w-4xl mx-auto p-8 text-stone-500">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="booking-page">
      <h1 className="font-serif text-4xl text-emerald-900 mb-2">Réserver une nuit</h1>
      <p className="text-stone-600 mb-6">Saison de chasse : 21 août → 31 janvier · 40 € la nuit · maximum 4 nuits par saison</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="hut-tabs">
        {huts.map(h => {
          const disabled = h.status !== "ACTIVE";
          const active = hut.id === h.id;
          return (
            <button key={h.id} onClick={() => { setParams({ hut: h.slug }); setSelected(null); }}
              disabled={disabled}
              data-testid={`hut-tab-${h.slug}`}
              className={`px-4 py-2 rounded-md text-sm border transition ${active ? "bg-emerald-800 text-white border-emerald-900" : "bg-white text-stone-700 border-stone-300 hover:border-emerald-700"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
              {h.name} {disabled && "· Travaux"}
            </button>
          );
        })}
      </div>

      {hut.status !== "ACTIVE" ? (
        <div className="p-6 rounded-xl bg-amber-50 border border-amber-200" data-testid="hut-disabled-block">
          <div className="font-serif text-xl text-amber-900 mb-1">Travaux en cours – ouverture prochaine</div>
          <p className="text-sm text-amber-900">Cette hutte n'est pas encore disponible à la réservation. Merci de votre patience.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="p-5 bg-white border border-stone-200 rounded-xl">
            <MonthNav month={month} year={year} onChange={(m,y)=>{setMonth(m);setYear(y);}} />
            <BookingCalendar days={days} month={month} year={year} selected={selected} onSelect={setSelected} />
            <CalendarLegend />
          </div>

          <div className="p-5 bg-white border border-stone-200 rounded-xl">
            <h2 className="font-serif text-xl text-emerald-900 mb-3">Vos informations</h2>
            {!selected ? (
              <div className="text-sm text-stone-500 py-6 text-center border-2 border-dashed rounded-md">Sélectionnez d'abord une date disponible dans le calendrier.</div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm">
                  <div className="font-semibold text-emerald-900">Nuit sélectionnée : {new Date(selected).toLocaleDateString("fr-FR", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}</div>
                  <div className="text-emerald-800">{hut.name} · {hut.price_per_night} €</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom du réservant" value={form.first_name} onChange={v=>setForm({...form,first_name:v})} testId="input-firstname"/>
                  <Field label="Nom du réservant" value={form.last_name} onChange={v=>setForm({...form,last_name:v})} testId="input-lastname"/>
                </div>
                <Field label="Email" type="email" value={form.email} onChange={v=>setForm({...form,email:v.toLowerCase()})} testId="input-email"/>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Téléphone" value={form.phone} onChange={v=>setForm({...form,phone:v})} testId="input-phone"/>
                  <Field label="Âge du réservant" type="number" value={form.age} onChange={v=>setForm({...form,age:v})} testId="input-age"/>
                </div>
                <Field label="Adresse postale du réservant" value={form.address} onChange={v=>setForm({...form,address:v})} testId="input-address"/>
                <div>
                  <label className="text-sm text-stone-600 block mb-1">Nombre de personnes (max {hut.max_capacity})</label>
                  <select className="w-full border rounded-md px-3 py-2" value={form.guests} onChange={e=>setGuests(Number(e.target.value))} data-testid="input-guests">
                    {Array.from({length: hut.max_capacity}, (_,i)=>i+1).map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-emerald-900 block mb-1">Occupants</label>
                  <p className="text-xs text-stone-500 mb-2">Renseignez chaque occupant (réservant inclus). Les occupants chasseurs doivent indiquer leur numéro de validation du permis de chasser en cours de validité.</p>
                  <div className="space-y-2">
                    {form.occupants.map((o, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-2 border border-stone-200 rounded-md bg-stone-50" data-testid={`occupant-${i}`}>
                        <input placeholder={`Prénom occupant ${i+1}`} className="border rounded-md px-2 py-1.5 text-sm" value={o.first_name} onChange={e=>setOccupant(i,"first_name",e.target.value)} data-testid={`occupant-${i}-firstname`}/>
                        <input placeholder="Nom" className="border rounded-md px-2 py-1.5 text-sm" value={o.last_name} onChange={e=>setOccupant(i,"last_name",e.target.value)} data-testid={`occupant-${i}-lastname`}/>
                        <input placeholder="N° permis (si chasseur)" className="border rounded-md px-2 py-1.5 text-sm" value={o.permit_number} onChange={e=>setOccupant(i,"permit_number",e.target.value)} data-testid={`occupant-${i}-permit`}/>
                      </div>
                    ))}
                  </div>
                </div>

                {quota && (
                  <div className={`text-sm p-2 rounded-md ${quota.used >= 4 ? "bg-red-50 text-red-800 border border-red-200" : "bg-stone-50 text-stone-700 border border-stone-200"}`} data-testid="quota-status">
                    Saison {quota.season} : {quota.used} / 4 nuits utilisées {quota.used >= 4 && "· Quota atteint"}
                  </div>
                )}

                {hut.boat_required && (
                  <label className="flex gap-2 items-start text-sm bg-amber-50 border border-amber-200 p-3 rounded-md">
                    <input type="checkbox" checked={form.boat_confirmed} onChange={e=>setForm({...form,boat_confirmed:e.target.checked})} data-testid="check-boat"/>
                    <span>J'ai bien pris connaissance que l'accès à la Hutte Petit Marais se fait exclusivement en barque et je confirme être en mesure d'utiliser cet accès.</span>
                  </label>
                )}

                <label className={`flex gap-2 items-start text-sm ${reg.pdf_url && !regOpened ? "opacity-50" : ""}`}>
                  <input type="checkbox" disabled={!!(reg.pdf_url && !regOpened)} checked={form.regulations_accepted} onChange={e=>setForm({...form,regulations_accepted:e.target.checked})} data-testid="check-regulations"/>
                  <span>Je certifie avoir <strong>ouvert et lu</strong> le règlement intérieur de la hutte pédagogique, l'avoir compris et m'engage à le respecter.
                  {reg.pdf_url && !regOpened && <span className="block text-xs text-amber-700 mt-1">Ouvrez d'abord le règlement (lien ci-dessous) pour activer cette case.</span>}</span>
                </label>
                {reg.pdf_url ? (
                  <a href={reg.pdf_url} target="_blank" rel="noreferrer" onClick={()=>setRegOpened(true)} data-testid="regulations-pdf-open"
                    className="flex items-center gap-2 p-3 border-2 border-emerald-700 bg-emerald-50 rounded-md text-emerald-900 font-semibold text-sm hover:bg-emerald-100 transition">
                    <FileText size={18}/> Ouvrir le règlement intérieur (PDF) — pièce jointe à lire avant de valider le paiement {regOpened && <span className="ml-auto text-emerald-700">✓ ouvert</span>}
                  </a>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">Le règlement intérieur (PDF) sera disponible prochainement.</div>
                )}

                <button disabled={!canSubmit} onClick={submit} data-testid="submit-booking-btn"
                  className={`w-full py-3 rounded-md font-semibold ${canSubmit ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-stone-200 text-stone-500 cursor-not-allowed"}`}>
                  {submitting ? "Redirection vers le paiement…" : `Payer ${hut.price_per_night} € et confirmer`}
                </button>
                <p className="text-xs text-stone-500 text-center">Paiement sécurisé par Stripe. Aucun débit avant validation.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type="text", testId }) {
  return (
    <div>
      <label className="text-sm text-stone-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} data-testid={testId}
        className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-600"/>
    </div>
  );
}
